import { useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';

import AdvanceFileInput from 'components/field/AdvanceFileInput';
import Status from 'components/field/Status';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Authorization from 'components/global/Authorization';
import Typography from 'components/global/Typography';

import useAccessControl from 'hooks/useAccessControl';

import BillingInfo from 'pages/Settings/General/BillingInfo';

import { ACCOUNT_TYPES } from 'utils/constants';

import BaseForm from './BaseForm';
import * as S from './Tabs.styled';

const defaultColSpan = {
	md: 6
};

const validateOptions = {
	shouldValidate: true,
	shouldDirty: true
};

const accountTypes = [
	{
		type: ACCOUNT_TYPES.COMPANY,
		description: 'otoLink.onboarding.CompanyDescription'
	},
	{
		type: ACCOUNT_TYPES.FREELANCER,
		description: 'otoLink.onboarding.FreelancerDescription',
		role: '/company-settings/CommonInvoiceType'
	},
	{
		type: ACCOUNT_TYPES.PERSONAL,
		description: 'otoLink.onboarding.PersonalDescription'
	}
];

const AccountType = () => {
	const isAuthorized = useAccessControl();
	const { watch, setValue, clearErrors } = useFormContext();
	const {
		VATNumber,
		CRNumber,
		CRDocumentURL,
		civilID,
		idNumber,
		fullName,
		taxOffice,
		companyName,
		kycRegisterStatus,
		invoiceType: userInvoiceType
	} = useSelector((state) => state?.user?.generalSettings);

	const accountType = watch('invoiceType');

	const handleChangeAccountType = (type) => {
		clearErrors();
		setValue('invoiceType', type, validateOptions);

		setValue('notVATRegistered', type !== ACCOUNT_TYPES.COMPANY, validateOptions);

		switch (type) {
			case ACCOUNT_TYPES.COMPANY:
				setValue('fullName', fullName || null, validateOptions);
				setValue('idNumber', idNumber || null, validateOptions);
				break;
			case ACCOUNT_TYPES.FREELANCER:
				setValue('companyName', companyName || null, validateOptions);
				setValue('VATNumber', VATNumber || null, validateOptions);
				setValue('taxOffice', taxOffice || null, validateOptions);
				setValue('CRNumber', CRNumber || null, validateOptions);
				setValue('CRDocumentURL', CRDocumentURL || null, validateOptions);
				break;
			case ACCOUNT_TYPES.PERSONAL:
				setValue('companyName', companyName || null, validateOptions);
				setValue('VATNumber', VATNumber || null, validateOptions);
				setValue('taxOffice', taxOffice || null, validateOptions);
				setValue('CRNumber', CRNumber || null, validateOptions);
				setValue('civilID', civilID || null, validateOptions);
				setValue('CRDocumentURL', CRDocumentURL || null, validateOptions);
				break;
			default:
				break;
		}
	};

	return (
		<S.SectionContainer>
			<BaseForm title="selectYourAccountType">
				<Alert content="selectYourAccountTypeWarning" showIcon className="mb-2-5" />
				<S.AccountTypeContainer>
					{accountTypes.map((account) => (
						<Authorization path={account.role} key={account.type}>
							<AccountTypeBox
								key={account.type}
								type={account.type}
								description={account.description}
								selectedType={accountType}
								isSelected={accountType === account.type}
								onClick={() => handleChangeAccountType(account.type)}
								kycRegisterStatus={kycRegisterStatus}
								invoiceType={userInvoiceType}
							/>
						</Authorization>
					))}
				</S.AccountTypeContainer>
			</BaseForm>
			{accountType !== ACCOUNT_TYPES.PERSONAL && <S.Line />}

			{accountType === ACCOUNT_TYPES.COMPANY && (
				<BaseForm title="uploadCompanyDocuments">
					<S.FormContainer>
						<FormItem
							roleName="/company-settings/CommonInvoiceType"
							colSpan={defaultColSpan}
							label="crNumber"
							required>
							<StringInput
								name="CRNumber"
								allowOnlyNumbers
								maxlength={isAuthorized('/company-settings/KSAInvoiceType') ? 10 : 9}
							/>
						</FormItem>
					</S.FormContainer>
					<S.FormContainer>
						<FormItem colSpan={defaultColSpan} label="CRDocumentLabel" required>
							<AdvanceFileInput
								name="CRDocumentURL"
								style={{ width: '100%' }}
								btnStyle={{ width: '100%' }}
								acceptedFileType={'*'}
								setValue={(value) => {
									setValue('CRDocumentURL', value?.path ? value : null, validateOptions);
								}}
								value={
									watch('CRDocumentURL') || {
										name: null,
										path: null
									}
								}
								maxFileSize={1000000}
							/>
						</FormItem>
						<FormItem
							roleName="/company-settings/KSAInvoiceType"
							colSpan={defaultColSpan}
							label="civilIDOrIqama"
							required>
							<AdvanceFileInput
								name="civilID"
								style={{ width: '100%' }}
								btnStyle={{ width: '100%' }}
								acceptedFileType={'*'}
								setValue={(value) => {
									setValue('civilID', value?.path ? value : null, validateOptions);
								}}
								value={
									watch('civilID') || {
										name: null,
										path: null
									}
								}
								maxFileSize={1000000}
							/>
						</FormItem>
					</S.FormContainer>
				</BaseForm>
			)}
			<Authorization path="/company-settings/CommonInvoiceType">
				{accountType === ACCOUNT_TYPES.FREELANCER && (
					<BaseForm title="uploadCompanyDocuments">
						<S.FormContainer>
							<FormItem colSpan={defaultColSpan} label="freelanceDocument" required>
								<AdvanceFileInput
									name="freelanceDocument"
									style={{ width: '100%' }}
									btnStyle={{ width: '100%' }}
									acceptedFileType={'*'}
									setValue={(value) => {
										setValue('freelanceDocument', value?.path ? value : null, validateOptions);
									}}
									value={
										watch('freelanceDocument') || {
											name: null,
											path: null
										}
									}
									maxFileSize={1000000}
								/>
							</FormItem>
							<FormItem colSpan={defaultColSpan} label="civilIDFrontSide" required>
								<AdvanceFileInput
									name="civilID"
									style={{ width: '100%' }}
									btnStyle={{ width: '100%' }}
									acceptedFileType={'*'}
									setValue={(value) => {
										setValue('civilID', value?.path ? value : null, validateOptions);
									}}
									value={
										watch('civilID') || {
											name: null,
											path: null
										}
									}
									maxFileSize={1000000}
								/>
							</FormItem>
						</S.FormContainer>
					</BaseForm>
				)}
			</Authorization>

			{accountType === ACCOUNT_TYPES.COMPANY && (
				<BaseForm title="billingAddress">
					<BillingInfo formFieldName="billingInfo" />
				</BaseForm>
			)}
		</S.SectionContainer>
	);
};

const AccountTypeBox = ({ type, description, onClick, isSelected, kycRegisterStatus, invoiceType }) => (
	<S.AccountTypeCard onClick={onClick} className={isSelected ? 'selected' : ''}>
		<S.AccountTypeContent>
			<S.AccountTypeHeader>
				<Typography variant="p2" fontWeight="500">
					{type}
				</Typography>
				{invoiceType === type && invoiceType !== ACCOUNT_TYPES.PERSONAL && <Status value={kycRegisterStatus} />}
			</S.AccountTypeHeader>
			<Typography color="var(--content-3)" noWrap>
				<Trans
					i18nKey={description}
					components={{
						bold: <Typography variant="caption" fontWeight="500" color="var(--content-3)" noWrap />
					}}
				/>
			</Typography>
		</S.AccountTypeContent>
		<S.StyledRadio readOnly checked={isSelected} />
	</S.AccountTypeCard>
);

export default AccountType;
